import type { SiteLang } from "@/hooks/useLanguage";

/* ───────────────────────────────────────────────────
   Spanish translations for Software Solutions & Services offerings.
   10 offerings from the spreadsheet.
   ─────────────────────────────────────────────────── */

export interface SwOfferingI18n {
  title: string;
  subtitle: string;
  description: string;
  faqItems?: { q: string; a: string }[];
  whatTitle: string;
  whatParagraphs: string[];
  whatImageAlt?: string;
  whyTitle: string;
  whyIntro?: string;
  whyBenefits: { title: string; text: string }[];
  helpCards?: {
    heading: string;
    intro: string;
    cards: { title: string; text: string }[];
  };
  processTitle?: string;
  processIntro?: string;
  processSteps?: { title: string; text: string }[];
  processImageAlt?: string;
  extraSections?: {
    title: string;
    paragraphs: string[];
    bullets?: { title: string; text: string }[];
    image?: string;
    imageAlt?: string;
    videoEmbedId?: string;
    vimeoUrl?: string;
  }[];
  externalCtaLabel?: string;
  whyImageAlt?: string;
  partnerTitle: string;
  partnerIntro: string;
  partnerBullets: string[];
  partnerImageAlt?: string;
}

const swOfferingsEs: Record<string, SwOfferingI18n> = {

  /* ───── Cumplimiento del Software Operativo ───── */
  "operational-software-compliance": {
    title: "Cumplimiento del Software Operativo",
    subtitle: "Mantén el cumplimiento durante todo el ciclo de vida del software",
    description:
      "Mantener el cumplimiento de los estándares regulatorios no es tarea fácil. Los servicios de cumplimiento del software operativo de QbD Group garantizan que tus sistemas de software cumplan los estándares y regulaciones de la industria, mejorando la seguridad de los datos y la fiabilidad del sistema.",
    whatTitle: "¿Qué es el cumplimiento del software operativo?",
    whatParagraphs: [
      "El cumplimiento del software operativo garantiza la <strong class='text-foreground'>adhesión continua a los estándares regulatorios, las mejores prácticas de la industria y las políticas internas durante la fase operativa</strong> de los sistemas de software. QbD Group ofrece soluciones robustas que aseguran que tus sistemas no solo cumplan los requisitos de cumplimiento, sino que también operen de manera eficiente y segura a lo largo de su ciclo de vida.",
      "Nuestros servicios incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Revisiones periódicas</strong>: evaluaciones regulares para verificar que tus sistemas permanecen en estado validado.</li><li><strong>Evaluaciones de impacto para actualizaciones de software</strong>: evaluación del impacto en el cumplimiento de parches, actualizaciones y cambios de configuración.</li><li><strong>Validaciones recurrentes</strong>: actividades de re-validación programadas para mantener el cumplimiento a lo largo del ciclo de vida del sistema.</li><li><strong>Gestión del control de cambios</strong>: garantía de que todos los cambios del sistema están documentados, evaluados y aprobados según los estándares regulatorios.</li><li><strong>Revisiones de audit trail</strong>: revisiones sistemáticas de las pistas de auditoría para mantener la integridad de los datos y el cumplimiento regulatorio.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante el cumplimiento del software operativo?",
    whyBenefits: [
      { title: "Mitiga riesgos de incumplimiento", text: "Evita sanciones legales, multas y daños reputacionales asegurando la adhesión a las regulaciones." },
      { title: "Protege la seguridad e integridad de los datos", text: "Protege los datos sensibles frente a brechas y accesos no autorizados." },
      { title: "Mantiene la fiabilidad del sistema", text: "Reduce el tiempo de inactividad, los errores y las interrupciones para operaciones ininterrumpidas." },
      { title: "Simplifica la complejidad regulatoria", text: "Proporciona un marco estructurado para gestionar los requisitos de cumplimiento." },
      { title: "Genera confianza en los stakeholders", text: "Demuestra un compromiso con las mejores prácticas, fomentando la confianza de clientes, socios y reguladores." },
    ],
    helpCards: {
      heading: "Beneficios del cumplimiento del software operativo",
      intro: "Asegura que tus sistemas cumplan los estándares regulatorios mientras mejoras la seguridad, la integridad y la confianza. El cumplimiento no solo protege los datos, sino que también refuerza el rendimiento operativo y reduce los riesgos.",
      cards: [
        { title: "Adhesión regulatoria", text: "Garantiza el cumplimiento de las leyes y estándares relevantes." },
        { title: "Seguridad de datos mejorada", text: "Protege los datos sensibles frente a brechas y accesos no autorizados." },
        { title: "Integridad operativa mejorada", text: "Mantiene la fiabilidad y funcionalidad del sistema." },
        { title: "Riesgos reducidos", text: "Identifica y aborda las brechas de cumplimiento para mitigar posibles problemas." },
        { title: "Mayor confianza de los stakeholders", text: "Demuestra un compromiso con el cumplimiento y las mejores prácticas." },
      ],
    },
    processTitle: "Nuestra forma de trabajar",
    processIntro: "En QbD Group, ofrecemos un enfoque integral del cumplimiento del software operativo, desde la evaluación hasta la implementación:",
    processSteps: [
      { title: "Evaluación inicial", text: "Comenzamos identificando y delimitando las actividades de validación recurrentes requeridas para tu software. Esto asegura una comprensión clara de tus necesidades y establece una base sólida para el éxito." },
      { title: "Implementación piloto", text: "Para refinar nuestro enfoque, iniciamos un proyecto piloto bajo un modelo de tiempo y materiales. Esto ayuda a validar las estimaciones de tiempo y recursos, afinar el proceso y confirmar el volumen de actividades de cumplimiento requeridas." },
      { title: "Planificación integral", text: "Utilizando los conocimientos del piloto, desarrollamos un plan detallado del proyecto para las actividades de cumplimiento continuas. Este plan incorpora herramientas de cumplimiento internas para apoyar el seguimiento y la ejecución de tareas." },
      { title: "Monitorización y soporte", text: "Nuestro soporte no se detiene con el plan. Proporcionamos monitorización continua y ajustes para asegurar que tus sistemas permanezcan conformes y validados." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Al colaborar con QbD Group, obtienes:",
    partnerBullets: [
      "Experiencia en cumplimiento — aprovecha el profundo conocimiento de QbD Group sobre requisitos de cumplimiento y procesos de validación.",
      "Carga de trabajo reducida — externaliza las actividades de cumplimiento para liberar recursos internos.",
      "Soluciones rentables — considera opciones nearshore u offshore para costes potencialmente más bajos.",
      "Herramientas y métodos probados — benefíciate de las herramientas y metodologías establecidas de QbD Group.",
    ],
  },

  /* ───── Digitalización ───── */
  "digitalization": {
    title: "Digitalización",
    subtitle: "Moderniza y optimiza tus operaciones con soluciones digitales conformes",
    description:
      "QbD Group ayuda a las empresas de Life Sciences a modernizar y optimizar sus operaciones. Nuestras soluciones de digitalización a medida garantizan una transición fluida hacia procesos más eficientes, ágiles y conformes, respaldando la integridad de los datos y el crecimiento a largo plazo.",
    faqItems: [
      { q: "¿Qué es la digitalización en Life Sciences?", a: "La digitalización en Life Sciences es el proceso de convertir información y procesos de formatos analógicos a digitales. Aprovechando tecnologías de vanguardia, las organizaciones transforman flujos de trabajo manuales y en papel en sistemas conformes, automatizados y basados en datos." },
      { q: "¿Cómo aborda QbD Group los proyectos de digitalización?", a: "Seguimos una metodología estructurada: evaluación de necesidades, selección de proveedor y producto, diseño de la solución, implementación del software, validación y cumplimiento, y gestión del cambio — asegurando una transición fluida en cada etapa." },
      { q: "¿Es QbD Group neutral respecto a los proveedores de tecnología?", a: "Sí. Adoptamos un enfoque tecnológicamente agnóstico, comparando una amplia gama de soluciones — desde herramientas sencillas hasta plataformas empresariales complejas — para recomendar la más adecuada según tus necesidades específicas, perfil de riesgo y presupuesto." },
      { q: "¿Cómo aseguráis el cumplimiento regulatorio durante la digitalización?", a: "Validamos cada sistema conforme al Anexo 11, 21 CFR Parte 11 y los estándares GAMP5 para garantizar la integridad de los datos y el cumplimiento regulatorio a lo largo de la transformación digital." },
      { q: "¿Qué es la asociación entre QbD Group y delaware?", a: "A través de nuestra asociación estratégica con delaware, combinamos una profunda experiencia en Life Sciences con tecnologías digitales de primer nivel. Juntos, ayudamos a las organizaciones a implementar sistemas potentes como SAP, completamente validados, conformes y alineados con tus procesos desde el primer día." },
    ],
    whatTitle: "¿Qué entendemos por digitalización en Life Sciences?",
    whatParagraphs: [
      "La digitalización es el proceso de <strong class='text-foreground'>convertir información y procesos de formatos analógicos a digitales</strong>. Aprovechando tecnologías de vanguardia, transformamos flujos de trabajo manuales y en papel en sistemas conformes, automatizados y basados en datos.",
      "Esta transformación ayuda a las organizaciones a <strong class='text-foreground'>aumentar la eficiencia, garantizar la trazabilidad e impulsar la innovación</strong>, todo ello reforzando el cumplimiento regulatorio y la experiencia del cliente. Nuestra experiencia en implementación de software, validación y asociaciones tecnológicas conecta los requisitos regulatorios, los procesos de negocio y la tecnología.",
    ],
    whyTitle: "Por qué la digitalización es importante para ti",
    whyBenefits: [
      { title: "Mayor eficiencia", text: "Procesos optimizados y trabajo manual reducido generan ahorros significativos de costes y mejoras en la productividad." },
      { title: "Mejora en la toma de decisiones", text: "Gestión de datos simplificada y accesibilidad mejorada respaldan decisiones informadas basadas en datos." },
      { title: "Agilidad mejorada", text: "Los sistemas digitalizados facilitan la adaptación a disrupciones del mercado y necesidades cambiantes de los clientes." },
    ],
    helpCards: {
      heading: "Beneficios de la digitalización",
      intro: "La digitalización ofrece mejoras tangibles en todas tus operaciones, desde la eficiencia y la innovación hasta la ventaja competitiva.",
      cards: [
        { title: "Adhesión regulatoria", text: "Garantiza el cumplimiento de las leyes y estándares relevantes." },
        { title: "Seguridad de datos mejorada", text: "Protege los datos sensibles frente a brechas y accesos no autorizados." },
        { title: "Integridad operativa mejorada", text: "Mantiene la fiabilidad y funcionalidad del sistema." },
        { title: "Riesgos reducidos", text: "Identifica y aborda las brechas de cumplimiento para mitigar posibles problemas." },
        { title: "Mayor confianza de los stakeholders", text: "Demuestra un compromiso con el cumplimiento y las mejores prácticas." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Al colaborar con QbD Group para tu digitalización:",
    partnerBullets: [
      "Experiencia profunda en Life Sciences — combinamos conocimiento regulatorio con experiencia tecnológica para una transformación digital conforme.",
      "Enfoque tecnológicamente agnóstico — comparamos todas las opciones para recomendar la mejor solución para tus necesidades.",
      "De principio a fin — acompañamos desde la evaluación de necesidades hasta la implementación, validación y soporte continuo.",
      "Asociación estratégica con delaware — acceso a tecnologías de primer nivel como SAP, completamente integradas y validadas.",
    ],
  },

  /* ───── Conformidad del software de dispositivos médicos ───── */
  "medical-device-software-compliance": {
    title: "Conformidad del software de dispositivos médicos",
    subtitle: "Asegura que tu software cumpla con MDR, IVDR, FDA e ISO",
    description:
      "Nuestro servicio de conformidad del software de dispositivos médicos ayuda a las empresas de MD e IVD a garantizar que su software se alinee con MDR, IVDR, FDA, ISO 13485, ISO 14971, IEC 62304 e IEC 82304. Con marcos a medida y orientación experta, simplificamos el cumplimiento.",
    whatTitle: "¿Qué es la conformidad del software de dispositivos médicos?",
    whatParagraphs: [
      "La conformidad del software de dispositivos médicos garantiza que <strong class='text-foreground'>tu software cumpla con los marcos regulatorios aplicables</strong>, incluidos MDR, IVDR, FDA, ISO 13485, ISO 14971, IEC 62304 e IEC 82304. QbD Group ofrece orientación experta y marcos a medida para simplificar el cumplimiento.",
      "Nuestros servicios cubren:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Análisis de brechas</strong>: identificación de áreas de no conformidad y recomendaciones de remediación.</li><li><strong>Clasificación del software</strong>: determinación de la clase de seguridad y los requisitos regulatorios aplicables.</li><li><strong>Documentación técnica</strong>: elaboración de ficheros técnicos conformes con MDR/IVDR y documentación FDA.</li><li><strong>Gestión de riesgos</strong>: procesos de análisis de riesgo alineados con ISO 14971.</li><li><strong>Ciclo de vida del software</strong>: soporte conforme a IEC 62304 para desarrollo, mantenimiento y gestión de cambios.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante la conformidad del software de MD?",
    whyBenefits: [
      { title: "Requisito regulatorio", text: "Los dispositivos médicos con software deben cumplir estrictos requisitos regulatorios para el acceso al mercado." },
      { title: "Seguridad del paciente", text: "Un software conforme protege a los pacientes garantizando fiabilidad y funcionamiento adecuado." },
      { title: "Acceso al mercado", text: "El cumplimiento es esencial para obtener marcado CE, autorización FDA y otras aprobaciones." },
      { title: "Gestión de riesgos", text: "Un enfoque estructurado reduce los riesgos asociados al fallo del software en entornos médicos." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "QbD Group proporciona soporte integral para la conformidad del software de dispositivos médicos:",
      cards: [
        { title: "Análisis de brechas regulatorio", text: "Identificación de no conformidades y definición de la hoja de ruta hacia el cumplimiento." },
        { title: "Documentación técnica", text: "Elaboración de ficheros técnicos conformes con MDR/IVDR y documentación FDA." },
        { title: "Gestión de riesgos", text: "Procesos de análisis de riesgo del software alineados con ISO 14971." },
        { title: "Soporte de ciclo de vida", text: "Acompañamiento conforme a IEC 62304 para desarrollo, mantenimiento y gestión de cambios." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para la conformidad del software de dispositivos médicos proporciona:",
    partnerBullets: [
      "Experiencia regulatoria profunda — conocimiento detallado de MDR, IVDR, FDA, IEC 62304 e ISO 14971.",
      "Enfoque práctico — marcos a medida adaptados a tu producto y contexto regulatorio.",
      "Soporte integral — desde el análisis de brechas hasta la documentación técnica y la preparación para auditorías.",
      "Trayectoria demostrada — experiencia con empresas de dispositivos médicos e IVD de todos los tamaños.",
    ],
  },

  /* ───── Cumplimiento IA/ML ───── */
  "ai-ml-compliance": {
    title: "Cumplimiento IA/ML",
    subtitle: "Navega las regulaciones de IA con confianza",
    description:
      "En el mundo en rápida evolución de la IA y el machine learning, desarrollar soluciones de software que cumplan los estándares regulatorios es crucial. QbD Group ofrece soporte integral de cumplimiento IA/ML para ayudarte a acortar tu camino al mercado.",
    whatTitle: "¿Qué es el cumplimiento IA/ML?",
    whatParagraphs: [
      "El cumplimiento IA/ML garantiza que <strong class='text-foreground'>las soluciones basadas en inteligencia artificial y machine learning cumplan con los marcos regulatorios aplicables</strong>, incluidos el AI Act de la UE, MDR/IVDR, FDA y las directrices de la EMA. QbD Group ofrece orientación experta para navegar este panorama regulatorio en evolución.",
      "Nuestros servicios incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Evaluación regulatoria de IA</strong>: análisis de los requisitos regulatorios aplicables a tu solución de IA/ML.</li><li><strong>Gestión de riesgos de IA</strong>: identificación y mitigación de riesgos específicos de los sistemas de IA.</li><li><strong>Validación de modelos de ML</strong>: verificación y validación de modelos de machine learning según estándares regulatorios.</li><li><strong>Documentación de conformidad</strong>: elaboración de la documentación requerida por el AI Act y otros marcos aplicables.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante el cumplimiento IA/ML?",
    whyBenefits: [
      { title: "Requisito regulatorio creciente", text: "El AI Act de la UE y otros marcos regulatorios exigen cumplimiento para los sistemas de IA en Life Sciences." },
      { title: "Confianza y transparencia", text: "El cumplimiento genera confianza en pacientes, reguladores y stakeholders." },
      { title: "Acceso al mercado", text: "La conformidad regulatoria es esencial para la aprobación y comercialización de soluciones basadas en IA." },
      { title: "Gestión de riesgos", text: "Un enfoque estructurado reduce los riesgos asociados a la IA en entornos regulados." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "QbD Group proporciona soporte integral para el cumplimiento de soluciones IA/ML en Life Sciences:",
      cards: [
        { title: "Evaluación regulatoria", text: "Análisis de los requisitos regulatorios aplicables a tu solución de IA/ML." },
        { title: "Gestión de riesgos de IA", text: "Identificación y mitigación de riesgos específicos de los sistemas de IA." },
        { title: "Validación de modelos", text: "Verificación y validación de modelos de ML según estándares regulatorios." },
        { title: "Documentación de conformidad", text: "Elaboración de la documentación requerida por el AI Act y otros marcos." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para el cumplimiento IA/ML proporciona:",
    partnerBullets: [
      "Experiencia de vanguardia — conocimiento actualizado del AI Act, MDR/IVDR y otros marcos regulatorios para IA.",
      "Enfoque práctico — orientación a medida adaptada a tu solución de IA/ML y contexto regulatorio.",
      "Visión integral — combinamos experiencia en software, dispositivos médicos y regulación para un soporte holístico.",
      "Preparación para el futuro — te ayudamos a anticiparte a las regulaciones emergentes en IA.",
    ],
  },

  /* ───── Gobernanza de datos ───── */
  "data-governance": {
    title: "Gobernanza de datos",
    subtitle: "Gestión estratégica de tus activos de datos para el cumplimiento y la eficiencia",
    description:
      "¿Te preocupa mantenerte al día con las regulaciones de datos complejas y cambiantes? QbD Group ofrece soporte de gobernanza de datos que te ayuda a cumplir con los últimos estándares y regulaciones, acortando tu camino al mercado.",
    whatTitle: "¿Qué es la gobernanza de datos?",
    whatParagraphs: [
      "La gobernanza de datos es el <strong class='text-foreground'>marco de políticas, procesos y tecnologías que garantizan la gestión efectiva, segura y conforme de los datos</strong> a lo largo de su ciclo de vida. QbD Group ayuda a las organizaciones de Life Sciences a establecer marcos de gobernanza robustos que garanticen el cumplimiento y la eficiencia.",
      "Nuestros servicios incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Estrategia de datos</strong>: definición de la visión, los objetivos y la hoja de ruta de gestión de datos de tu organización.</li><li><strong>Calidad de datos</strong>: implementación de procesos para asegurar la precisión, completitud y consistencia de los datos.</li><li><strong>Protección de datos</strong>: garantía del cumplimiento de GDPR y otras regulaciones de privacidad de datos.</li><li><strong>Arquitectura de datos</strong>: diseño de marcos que soporten la integración, accesibilidad y escalabilidad de los datos.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante la gobernanza de datos?",
    whyBenefits: [
      { title: "Cumplimiento regulatorio", text: "Cumple con GDPR, Anexo 11, 21 CFR Parte 11 y otros requisitos de gestión de datos." },
      { title: "Calidad de datos mejorada", text: "Datos precisos, completos y consistentes mejoran la toma de decisiones y los resultados." },
      { title: "Eficiencia operativa", text: "Procesos de datos optimizados reducen redundancias y mejoran la productividad." },
      { title: "Gestión de riesgos", text: "Un marco robusto reduce los riesgos asociados a las brechas de datos y el incumplimiento." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "QbD Group proporciona soporte integral de gobernanza de datos para organizaciones de Life Sciences:",
      cards: [
        { title: "Estrategia de datos", text: "Definición de la visión y la hoja de ruta de gestión de datos de tu organización." },
        { title: "Calidad de datos", text: "Implementación de procesos para asegurar datos precisos, completos y consistentes." },
        { title: "Protección de datos", text: "Garantía del cumplimiento de GDPR y regulaciones de privacidad." },
        { title: "Arquitectura de datos", text: "Diseño de marcos que soporten integración, accesibilidad y escalabilidad." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para la gobernanza de datos proporciona:",
    partnerBullets: [
      "Experiencia en datos regulados — profundo conocimiento de los requisitos de gestión de datos en Life Sciences.",
      "Enfoque holístico — abordamos la gobernanza de datos desde la estrategia hasta la implementación técnica.",
      "Soluciones a medida — adaptamos nuestros marcos a las necesidades específicas de tu organización.",
      "Soporte continuo — acompañamiento durante todo el ciclo de vida de tus datos.",
    ],
  },

  /* ───── Validación de software y sistemas informáticos ───── */
  "software-validation": {
    title: "Validación de software y sistemas informáticos centralizados",
    subtitle: "Servicios expertos de CSV para entornos regulados GxP",
    description:
      "La validación de software es más crítica que nunca, desde la automatización y la digitalización hasta la inteligencia artificial y el machine learning. QbD Group ofrece experiencia en validación de software y CSV para ayudar a tu empresa a cumplir con las últimas regulaciones.",
    whatTitle: "¿Qué es la validación de software y sistemas informáticos centralizados?",
    whatParagraphs: [
      "La validación de software garantiza que <strong class='text-foreground'>los sistemas informatizados utilizados en entornos GxP funcionan de manera fiable, coherente y conforme</strong> a los requisitos regulatorios. QbD Group ofrece servicios expertos de CSV que abarcan desde la planificación hasta la cualificación operativa y el soporte continuo.",
      "Nuestros servicios incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Validación de sistemas informatizados (CSV)</strong>: enfoque basado en riesgos conforme a GAMP5 para validar sistemas GxP.</li><li><strong>Cualificación de sistemas centralizados de TI</strong>: validación de ERP, LIMS, QMS y otros sistemas empresariales.</li><li><strong>Evaluación de proveedores de software</strong>: auditorías y evaluaciones de proveedores para asegurar la conformidad del software adquirido.</li><li><strong>Soporte de cumplimiento continuo</strong>: revisiones periódicas, gestión de cambios y re-validación.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante la validación de software?",
    whyBenefits: [
      { title: "Requisito regulatorio", text: "La validación de software es obligatoria según el Anexo 11, 21 CFR Parte 11 y las directrices GAMP5." },
      { title: "Integridad de los datos", text: "Garantiza que los datos generados y gestionados por los sistemas son fiables y trazables." },
      { title: "Reducción de riesgos", text: "Un enfoque basado en riesgos minimiza los fallos del sistema y las no conformidades." },
      { title: "Eficiencia operativa", text: "Sistemas validados funcionan de manera más fiable, reduciendo el tiempo de inactividad y los errores." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "QbD Group proporciona servicios integrales de validación de software para entornos GxP:",
      cards: [
        { title: "CSV basado en riesgos", text: "Enfoque conforme a GAMP5 para validar sistemas informatizados de manera eficiente." },
        { title: "Cualificación de sistemas centralizados", text: "Validación de ERP, LIMS, QMS y otros sistemas empresariales." },
        { title: "Evaluación de proveedores", text: "Auditorías y evaluaciones para asegurar la conformidad del software adquirido." },
        { title: "Soporte continuo", text: "Revisiones periódicas, gestión de cambios y re-validación." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para la validación de software proporciona:",
    partnerBullets: [
      "Décadas de experiencia — amplia trayectoria en CSV y validación de sistemas informatizados en Life Sciences.",
      "Enfoque basado en riesgos — metodología GAMP5 que optimiza el esfuerzo de validación según el riesgo.",
      "Cobertura integral — desde la planificación hasta la cualificación operativa y el soporte continuo.",
      "Equipo experto — profesionales certificados con experiencia en múltiples industrias y tecnologías.",
    ],
  },

  /* ───── Integridad de datos ───── */
  "data-integrity": {
    title: "Integridad de datos",
    subtitle: "Asegura la precisión y consistencia a lo largo del ciclo de vida de los datos",
    description:
      "La integridad de datos es más crítica que nunca, desde equipos y sistemas de laboratorio hasta sistemas automatizados e infraestructura de TI. QbD Group ofrece experiencia personalizada para ayudar a tu empresa a cumplir con las últimas regulaciones.",
    whatTitle: "¿Qué es la integridad de datos?",
    whatParagraphs: [
      "La integridad de datos garantiza que <strong class='text-foreground'>los datos son completos, consistentes, precisos y fiables a lo largo de todo su ciclo de vida</strong>. En entornos regulados, es un requisito fundamental para la conformidad GxP y la toma de decisiones basada en datos.",
      "Nuestros servicios incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Evaluación de integridad de datos</strong>: análisis de brechas para identificar riesgos y oportunidades de mejora.</li><li><strong>Revisión de audit trail</strong>: revisiones sistemáticas de pistas de auditoría para asegurar la trazabilidad.</li><li><strong>Remediación</strong>: acciones correctivas y preventivas para abordar hallazgos de integridad de datos.</li><li><strong>Formación</strong>: programas de formación para asegurar que tu equipo comprende los principios ALCOA+.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante la integridad de datos?",
    whyBenefits: [
      { title: "Requisito regulatorio", text: "La integridad de datos es una expectativa fundamental de las autoridades reguladoras como la EMA, la FDA y la MHRA." },
      { title: "Seguridad del paciente", text: "Datos fiables son esenciales para garantizar la seguridad y eficacia de los productos." },
      { title: "Confianza regulatoria", text: "Una gestión sólida de la integridad de datos fortalece la confianza de los reguladores en tu organización." },
      { title: "Eficiencia operativa", text: "Datos íntegros reducen la repetición de trabajo y mejoran la productividad." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "QbD Group proporciona soporte integral para la integridad de datos en entornos regulados:",
      cards: [
        { title: "Evaluación de brechas", text: "Análisis de la situación actual e identificación de riesgos y oportunidades de mejora." },
        { title: "Revisión de audit trail", text: "Revisiones sistemáticas de pistas de auditoría para asegurar trazabilidad y conformidad." },
        { title: "Remediación", text: "Acciones correctivas y preventivas para abordar hallazgos de integridad de datos." },
        { title: "Formación ALCOA+", text: "Programas de formación para asegurar que tu equipo comprende y aplica los principios ALCOA+." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para la integridad de datos proporciona:",
    partnerBullets: [
      "Experiencia regulatoria profunda — amplio conocimiento de las expectativas de la EMA, FDA y MHRA en materia de integridad de datos.",
      "Enfoque práctico — soluciones a medida adaptadas a tu entorno y nivel de madurez.",
      "Soporte integral — desde la evaluación hasta la remediación y la formación del equipo.",
      "Trayectoria demostrada — experiencia exitosa con organizaciones de todos los tamaños en Life Sciences.",
    ],
  },

  /* ───── Implementaciones de software ───── */
  "software-implementations": {
    title: "Implementaciones de software",
    subtitle: "Adopta e integra software de forma fluida en tu flujo de trabajo",
    description:
      "Las implementaciones de software son esenciales para que las organizaciones modernas de Life Sciences sigan siendo competitivas. Nuestro equipo de expertos en software asegura que tu proyecto de implementación sea exitoso, conforme y responda a las necesidades de tu organización.",
    whatTitle: "¿Qué son las implementaciones de software?",
    whatParagraphs: [
      "Las implementaciones de software abarcan el <strong class='text-foreground'>proceso de planificación, configuración, validación y despliegue de sistemas de software</strong> en entornos regulados de Life Sciences. QbD Group proporciona soporte de principio a fin para asegurar una adopción fluida y conforme.",
      "Nuestros servicios incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Selección de software</strong>: evaluación y comparación de soluciones para encontrar la mejor opción para tu organización.</li><li><strong>Gestión de proyectos</strong>: planificación y coordinación integral del proyecto de implementación.</li><li><strong>Configuración y personalización</strong>: adaptación del software a tus procesos y requisitos específicos.</li><li><strong>Validación y cualificación</strong>: aseguramiento de que el sistema cumple los requisitos regulatorios y funcionales.</li><li><strong>Formación y gestión del cambio</strong>: capacitación del equipo y gestión de la transición al nuevo sistema.</li></ul>",
    ],
    whyTitle: "¿Por qué son importantes las implementaciones de software?",
    whyBenefits: [
      { title: "Eficiencia operativa", text: "Sistemas bien implementados optimizan procesos y reducen el trabajo manual." },
      { title: "Cumplimiento regulatorio", text: "Una implementación conforme garantiza la adhesión a GxP y otros estándares." },
      { title: "Retorno de inversión", text: "Una implementación exitosa maximiza el valor del software y minimiza los riesgos." },
      { title: "Preparación para el futuro", text: "Sistemas modernos preparan a tu organización para los retos tecnológicos futuros." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "QbD Group proporciona soporte integral para implementaciones de software en Life Sciences:",
      cards: [
        { title: "Selección de software", text: "Evaluación y comparación de soluciones para encontrar la más adecuada." },
        { title: "Gestión de proyectos", text: "Planificación y coordinación integral del proyecto de implementación." },
        { title: "Configuración y validación", text: "Adaptación del software y aseguramiento del cumplimiento regulatorio." },
        { title: "Formación y cambio", text: "Capacitación del equipo y gestión de la transición al nuevo sistema." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para implementaciones de software proporciona:",
    partnerBullets: [
      "Experiencia multidisciplinar — combinamos conocimiento de Life Sciences con experiencia en TI.",
      "Enfoque tecnológicamente agnóstico — seleccionamos la mejor solución para tus necesidades, sin limitaciones de proveedor.",
      "Soporte de principio a fin — desde la selección del software hasta la formación del equipo y el soporte post-implementación.",
      "Trayectoria demostrada — amplia experiencia en implementaciones de LIMS, QMS, ERP y más.",
    ],
  },

  /* ───── Cualificación de la infraestructura informática ───── */
  "it-infrastructure-qualification": {
    title: "Cualificación de la infraestructura informática",
    subtitle: "Demuestra la adecuación de los componentes de TI para el cumplimiento GxP",
    description:
      "En el entorno altamente regulado de hoy, asegurar la fiabilidad y el cumplimiento de la infraestructura de TI es un reto creciente. QbD Group ofrece experiencia en cualificación de infraestructura informática para ayudar a tu empresa a cumplir con las últimas regulaciones.",
    whatTitle: "¿Qué es la cualificación de la infraestructura informática?",
    whatParagraphs: [
      "La cualificación de la infraestructura informática es el <strong class='text-foreground'>proceso de demostrar que los componentes de TI subyacentes — servidores, redes, almacenamiento, virtualización — son adecuados para su uso en entornos GxP</strong>. QbD Group proporciona soporte experto para cualificar tu infraestructura de TI según los estándares regulatorios aplicables.",
      "Nuestros servicios incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Cualificación de servidores</strong>: verificación de que los servidores cumplen los requisitos de rendimiento y seguridad.</li><li><strong>Cualificación de redes</strong>: validación de la infraestructura de red para asegurar la conectividad y la seguridad.</li><li><strong>Cualificación de entornos virtualizados</strong>: aseguramiento de que las plataformas de virtualización son adecuadas para sistemas GxP.</li><li><strong>Cualificación de almacenamiento</strong>: verificación de la integridad y disponibilidad de los sistemas de almacenamiento de datos.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante la cualificación de la infraestructura informática?",
    whyBenefits: [
      { title: "Base del cumplimiento GxP", text: "Los sistemas GxP se apoyan en una infraestructura de TI cualificada; sin ella, el cumplimiento es incompleto." },
      { title: "Integridad de datos", text: "Una infraestructura cualificada asegura que los datos se almacenan y transmiten de forma fiable." },
      { title: "Disponibilidad del sistema", text: "Reduce el riesgo de interrupciones que afecten a las operaciones reguladas." },
      { title: "Preparación para auditorías", text: "Documentación completa que demuestra la adecuación de tu infraestructura ante reguladores." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "QbD Group proporciona soporte integral para la cualificación de tu infraestructura informática:",
      cards: [
        { title: "Cualificación de servidores", text: "Verificación del rendimiento y la seguridad de tus servidores." },
        { title: "Cualificación de redes", text: "Validación de la infraestructura de red para conectividad y seguridad." },
        { title: "Entornos virtualizados", text: "Aseguramiento de la adecuación de las plataformas de virtualización para GxP." },
        { title: "Cualificación de almacenamiento", text: "Verificación de la integridad y disponibilidad de los sistemas de almacenamiento." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para la cualificación de infraestructura informática proporciona:",
    partnerBullets: [
      "Experiencia en infraestructura GxP — profundo conocimiento de los requisitos de cualificación para entornos regulados.",
      "Enfoque basado en riesgos — optimizamos el esfuerzo de cualificación según el nivel de riesgo de cada componente.",
      "Documentación completa — dossier de cualificación que demuestra la adecuación ante reguladores.",
      "Soporte continuo — acompañamiento durante la recualificación periódica y los cambios de infraestructura.",
    ],
  },

  /* ───── Validación sin papel ───── */
  "paperless-validation": {
    title: "Validación sin papel",
    subtitle: "Revoluciona tu proceso de validación con soluciones digitales",
    description:
      "La validación sin papel ofrece un enfoque digital que revoluciona el proceso de validación en industrias reguladas. Despídete de la documentación en papel y da la bienvenida a una eficiencia mejorada, una validación acelerada y una integridad de datos reforzada.",
    whatTitle: "¿Qué es la validación sin papel?",
    whatParagraphs: [
      "La validación sin papel es el <strong class='text-foreground'>enfoque digital que transforma los procesos de validación tradicionales basados en papel en flujos de trabajo electrónicos</strong>, mejorando la eficiencia, la trazabilidad y la integridad de los datos. QbD Group ayuda a las organizaciones a adoptar este enfoque de forma conforme y efectiva.",
      "Nuestros servicios incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Digitalización de protocolos de validación</strong>: conversión de documentos de validación en papel a formatos electrónicos conformes.</li><li><strong>Implementación de herramientas de validación digital</strong>: despliegue de plataformas que automatizan la ejecución y el seguimiento de la validación.</li><li><strong>Gestión electrónica de firmas</strong>: implementación de firmas electrónicas conformes con 21 CFR Parte 11 y Anexo 11.</li><li><strong>Formación y cambio</strong>: capacitación del equipo para la adopción del nuevo enfoque digital.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante la validación sin papel?",
    whyBenefits: [
      { title: "Eficiencia acelerada", text: "Los procesos digitales reducen significativamente el tiempo de ejecución de la validación." },
      { title: "Integridad de datos mejorada", text: "Los registros electrónicos ofrecen mejor trazabilidad y reducen los errores humanos." },
      { title: "Reducción de costes", text: "Elimina los costes asociados a la impresión, almacenamiento y gestión de documentación en papel." },
      { title: "Sostenibilidad", text: "Contribuye a los objetivos de sostenibilidad de tu organización reduciendo el uso de papel." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "QbD Group proporciona soporte integral para la transición a la validación sin papel:",
      cards: [
        { title: "Digitalización de protocolos", text: "Conversión de documentos de validación en papel a formatos electrónicos conformes." },
        { title: "Herramientas digitales", text: "Despliegue de plataformas que automatizan la ejecución y el seguimiento de la validación." },
        { title: "Firmas electrónicas", text: "Implementación de firmas electrónicas conformes con 21 CFR Parte 11 y Anexo 11." },
        { title: "Formación y cambio", text: "Capacitación del equipo para la adopción del enfoque digital." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para la validación sin papel proporciona:",
    partnerBullets: [
      "Experiencia en validación digital — pioneros en la adopción de enfoques paperless en Life Sciences.",
      "Cumplimiento garantizado — todos los procesos digitales cumplen con 21 CFR Parte 11 y Anexo 11.",
      "Herramientas propias — acceso a plataformas de validación digital probadas y establecidas.",
      "Soporte integral — desde la evaluación inicial hasta la implementación y la formación del equipo.",
    ],
  },
};

const swOfferingsZh: Record<string, SwOfferingI18n> = {

  /* ───── 医疗器械软件合规 ───── */
  "medical-device-software-compliance": {
    title: "医疗器械软件合规",
    subtitle: "确保您的软件符合 MDR、IVDR、FDA 和 ISO 要求",
    description:
      "我们的医疗器械软件合规服务帮助 Medical Devices 与 In Vitro Diagnostics 企业确保其软件符合 MDR、IVDR、FDA、ISO 13485、ISO 14971、IEC 62304 和 IEC 82304。借助量身定制的框架和专家指导，我们让合规变得更简单。",
    whatTitle: "什么是医疗器械软件合规？",
    whatParagraphs: [
      "医疗器械软件合规可确保<strong class='text-foreground'>您的软件符合适用的法规框架</strong>，包括 MDR、IVDR、FDA、ISO 13485、ISO 14971、IEC 62304 和 IEC 82304。QbD Group 提供专家指导和量身定制的框架，让合规更简单。",
      "我们的服务涵盖：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>差距分析</strong>：识别不合规领域并提出整改建议。</li><li><strong>软件分类</strong>：确定安全等级以及适用的法规要求。</li><li><strong>技术文件</strong>：编制符合 MDR/IVDR 的技术文件及 FDA 文档。</li><li><strong>风险管理</strong>：与 ISO 14971 保持一致的风险分析流程。</li><li><strong>软件生命周期</strong>：依据 IEC 62304 提供开发、维护与变更管理支持。</li></ul>",
    ],
    whyTitle: "为什么医疗器械软件合规很重要？",
    whyBenefits: [
      { title: "法规要求", text: "含软件的医疗器械必须满足严格的法规要求，才能进入市场。" },
      { title: "患者安全", text: "合规的软件可确保可靠性和正确运行，从而保护患者。" },
      { title: "市场准入", text: "合规是获得 CE 标志、FDA 批准及其他许可的关键前提。" },
      { title: "风险管理", text: "结构化方法可降低医疗环境中软件故障所带来的风险。" },
    ],
    partnerTitle: "为什么选择与 QbD Group 合作？",
    partnerIntro: "在医疗器械软件合规方面与 QbD Group 合作，您将获得：",
    partnerBullets: [
      "深厚的法规专业能力 — 对 MDR、IVDR、FDA、IEC 62304 与 ISO 14971 的细致理解。",
      "务实的方法 — 根据您的产品和法规背景量身定制的框架。",
      "端到端支持 — 从差距分析到技术文件，再到审计准备。",
      "可验证的业绩 — 服务于各种规模的 Medical Devices 与 In Vitro Diagnostics 企业的丰富经验。",
    ],
  },

  /* ───── AI/ML 合规 ───── */
  "ai-ml-compliance": {
    title: "AI/ML 合规",
    subtitle: "自信应对 AI 法规",
    description:
      "在 AI 与机器学习快速发展的背景下，开发符合法规标准的软件解决方案至关重要。QbD Group 提供全面的 AI/ML 合规支持，助您缩短上市路径。",
    whatTitle: "什么是 AI/ML 合规？",
    whatParagraphs: [
      "仅依靠数据治理已不足以应对 AI 带来的独特挑战。AI 系统通常以黑箱方式运行，难以追踪其决策过程和潜在偏差。AI 模型缺乏透明度和可解释性，引发了对公平性、问责制和伦理的担忧。",
      "AI/ML 合规涵盖<strong class='text-foreground'>数据治理，同时扩展其范围以解决特定的 AI 相关问题</strong>。AI 治理的关键组成部分包括<strong class='text-foreground'>算法透明度、可解释性、公平性、问责制以及遵守 AI 相关法规</strong>。推动这项合规服务的主要驱动力是 AI Act。",
    ],
    whatImageAlt: "AI/ML 合规 — Software Solutions & Services — QbD Group",
    whyTitle: "为什么 AI/ML 合规很重要？",
    whyIntro: "确保 AI 与机器学习（AI/ML）合规至关重要，原因众多，尤其是在生命科学领域，数据完整性与患者安全至关重要。随着 AI Act 等法规的出台，合规不仅是法律义务，更是构建可信赖且有效 AI 解决方案的基石。",
    whyBenefits: [
      { title: "AI 的伦理使用", text: "合规确保 AI/ML 驱动的 IVD 与 MD 系统以伦理方式运行，防范可能影响患者护理和数据安全的偏差、歧视和隐私泄露。" },
      { title: "风险缓解", text: "有效的 AI/ML 合规框架可最大限度降低数据泄露风险、法规问题和声誉损害，同时保护患者数据完整性。" },
      { title: "信任与问责", text: "负责任的 AI/ML 使用能够建立信任，这对于促进应用落地、推动协作以及在受监管的数据驱动环境中支持创新至关重要。" },
    ],
    processTitle: "我们如何推进 AI/ML 合规",
    processIntro: "AI/ML 合规是一个新兴领域，由近期发布的 AI Act 推动：",
    processSteps: [
      { title: "现状分析", text: "我们首先了解您当前的工作方式以及使用的工具。这可以通过设计研究来完成，也可以作为项目的第一步。" },
      { title: "程序制定", text: "我们将编写程序，在法规合规与贵组织业务效率之间搭建桥梁。" },
      { title: "审查与建议", text: "我们提供建议，并可以审查您产品有关数据与 AI 治理的技术文件。" },
    ],
    extraSections: [
      {
        title: "AI/ML 合规至关重要",
        paragraphs: [
          "AI/ML 合规是一个由近期发布的 AI Act 推动的新兴领域，它确保采用最新的<strong class='text-foreground'>先进技术标准</strong>，以满足公告机构对产品认可的要求。",
          "总体而言，AI/ML 合规对于培养<strong class='text-foreground'>负责任的数据管理</strong>文化、推动创新以及实现数字化时代的可持续业务增长至关重要。",
        ],
      },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "AI/ML 合规由法规（MDR、IVDR 以及即将出台的 AI Act）强制要求，并由公告机构进行验证。您仍然可以选择如何整合您的工作方式以及作为数据和 AI 治理一部分所使用的工具。QbD Group 可以提供战略建议和支持，以确保合规与业务效率兼顾。",
    partnerBullets: [
      "战略建议 — 将 AI/ML 合规融入您现有的工作流程与工具中。",
      "法规专业能力 — 深入了解 MDR、IVDR 与 AI Act 的要求。",
      "先进技术标准 — 多项正在制定中的标准，确保我们始终掌握 AI/ML 合规的最新动态。",
      "灵活的合作模式 — 从全面的 AI 治理到特定领域的针对性指导。",
      "专家咨询 — 与我们的专家预约咨询，评估您的具体需求，讨论实施策略，并了解我们的服务如何使您的组织受益。",
    ],
  },

  /* ───── 数据治理 ───── */
  "data-governance": {
    title: "数据治理",
    subtitle: "为您的数据资产提供战略性管理，以确保合规与高效",
    description:
      "您是否担心难以跟上复杂且不断变化的法规要求？QbD Group 提供数据治理支持，帮助您符合最新标准与法规，并缩短上市路径。",
    whatTitle: "什么是数据治理？",
    whatParagraphs: [
      "数据治理是指<strong class='text-foreground'>对数据资产进行战略性管理，涵盖数据质量、安全、隐私与合规</strong>。它包括制定数据策略、明确数据管理的角色与职责、设定数据质量标准，以及实施数据保护措施。",
      "这一过程确保<strong class='text-foreground'>数据保持可信、可访问且一致</strong>，为成功的数据驱动型项目奠定基础。数据治理确保<strong class='text-foreground'>符合 MD/IVD 法规与 AI Act 的要求</strong>。作为 QbD Group，我们可以帮助确保这些流程适合贵公司的工作方式，因为不存在适用于所有需求的单一解决方案。",
    ],
    whatImageAlt: "数据治理 — Software Solutions & Services — QbD Group",
    whyTitle: "为什么数据治理很重要？",
    whyIntro: "有效的数据治理对于确保数据与人工智能的负责任使用至关重要。它帮助组织在当今数据驱动的世界中维护伦理标准、降低风险，并与利益相关者建立信任。",
    whyBenefits: [
      { title: "数据的伦理使用", text: "数据治理确保数据与 AI 系统以合乎伦理的方式使用，防范偏见、歧视和隐私泄露。" },
      { title: "风险缓解", text: "恰当的治理可降低数据泄露、法规不合规以及因数据滥用或存在偏差的 AI 算法而造成声誉损害的风险。" },
      { title: "信任与问责", text: "治理通过透明度、问责制以及负责任的数据与 AI 使用来建立信任，这对于在数据驱动环境中的落地应用和协作至关重要。" },
    ],
    whyImageAlt: "数据治理的重要性 — QbD Group",
    helpCards: {
      heading: "数据治理的优势",
      intro: "实施稳健的数据治理可在合规、质量与运营效率方面带来可衡量的提升。",
      cards: [
        { title: "合规", text: "确保遵守法规与标准，降低因数据滥用而产生的法律风险和处罚。" },
        { title: "数据质量", text: "提升数据的准确性、一致性与可靠性，从而改善决策和业务成果。" },
        { title: "风险管理", text: "识别并缓解与数据泄露、隐私侵犯和算法偏见相关的风险。" },
        { title: "信任与透明", text: "通过透明的数据实践和 AI 决策流程，在利益相关者之间建立信任。" },
        { title: "效率", text: "简化数据管理流程，减少冗余并优化资源配置。" },
      ],
    },
    processTitle: "我们如何支持数据治理",
    processIntro: "您可以选择如何整合您的工作方式以及在数据与 AI 治理中使用的工具。QbD Group 可以提供战略建议和支持，以确保兼顾合规与业务效率：",
    processSteps: [
      { title: "设计研究", text: "我们首先了解您当前的工作方式以及使用的工具。这可以通过设计研究来完成，也可以作为项目的第一步。" },
      { title: "程序制定", text: "我们将编写程序，在法规合规与贵组织业务效率之间搭建桥梁。" },
      { title: "技术审查", text: "我们提供建议，并可以审查您产品有关数据与 AI 治理的技术文件。" },
      { title: "专家咨询", text: "与我们的专家预约咨询，评估您的具体需求，讨论实施策略，并了解我们的服务如何使您的组织受益。" },
    ],
    processImageAlt: "数据治理流程 — QbD Group",
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "我们可以通过以下方式帮助您实现数据治理的优势：",
    partnerBullets: [
      "战略建议 — 将数据治理融入您现有的工作流程。",
      "法规专业能力 — 确保符合 MDR/IVDR 以及即将出台的 AI Act。",
      "流程设计 — 在合规与业务效率之间搭建桥梁。",
      "技术审查 — 为您的技术文件提供专家建议。",
    ],
    partnerImageAlt: "为什么选择 QbD Group — 数据治理",
  },

  "software-validation": {
    title: "软件与中央 IT 系统验证",
    subtitle: "面向 GxP 受监管环境的专业 CSV 服务",
    description:
      "从自动化、数字化到人工智能与机器学习，软件验证依然至关重要。QbD Group 提供软件验证与 CSV 专业能力，帮助贵公司符合最新法规要求。",
    whatTitle: "什么是软件与中央 IT 系统验证？",
    whatParagraphs: [
      "软件验证旨在确保<strong class='text-foreground'>用于 GxP 环境的计算机化系统能够可靠、稳定且合规地</strong>满足法规要求。QbD Group 提供从规划、运行确认到持续支持的全方位专业 CSV 服务。",
      "我们的服务包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>计算机化系统验证 (CSV)</strong>：依据 GAMP5 的基于风险的方法验证 GxP 系统。</li><li><strong>中央 IT 系统确认</strong>：对 ERP、LIMS、QMS 及其他企业级系统进行验证。</li><li><strong>软件供应商评估</strong>：对供应商进行审计与评估，以确保所采购软件的合规性。</li><li><strong>持续合规支持</strong>：定期回顾、变更管理与再验证。</li></ul>",
    ],
    whyTitle: "为什么软件验证很重要？",
    whyBenefits: [
      { title: "法规要求", text: "根据 Annex 11、21 CFR Part 11 与 GAMP5 指南，软件验证是强制要求。" },
      { title: "数据完整性", text: "确保由系统生成与管理的数据可靠且可追溯。" },
      { title: "风险降低", text: "基于风险的方法可最大限度减少系统故障与不合规。" },
      { title: "运营效率", text: "经过验证的系统运行更可靠，可减少停机与错误。" },
    ],
    helpCards: {
      heading: "我们可以如何帮助您？",
      intro: "QbD Group 为 GxP 环境提供全面的软件验证服务：",
      cards: [
        { title: "基于风险的 CSV", text: "采用符合 GAMP5 的方法高效验证计算机化系统。" },
        { title: "中央系统确认", text: "对 ERP、LIMS、QMS 及其他企业级系统进行验证。" },
        { title: "供应商评估", text: "通过审计与评估确保所采购软件的合规性。" },
        { title: "持续支持", text: "定期回顾、变更管理与再验证。" },
      ],
    },
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "选择 QbD Group 进行软件验证，您将获得：",
    partnerBullets: [
      "数十年经验 — 在生命科学领域的 CSV 与计算机化系统验证方面拥有深厚积累。",
      "基于风险的方法 — 依据 GAMP5 方法论，按风险等级优化验证工作量。",
      "全面覆盖 — 从规划到运行确认与持续支持。",
      "专家团队 — 在多个行业与技术领域具备认证经验的专业人员。",
    ],
  },

  /* ───── IFUcare ───── */
  "ifucare": {
    title: "IFUcare — 电子 IFU 解决方案",
    subtitle: "面向医疗器械与 IVD 的电子 IFU 解决方案",
    description:
      "IFUcare 为医疗器械和 IVD 制造商提供无缝的电子 IFU 服务，帮助您告别纸质 IFU，转向合规且经济高效的电子标签。",
    whatTitle: "面向医疗器械与 IVD 的电子 IFU 解决方案",
    whatParagraphs: [
      "贵组织是否在为纸质使用说明（IFU）所带来的挑战而苦恼？从高昂的印刷和物流成本，到复杂的合规要求，传统的 IFU 对于体外诊断（IVD）和医疗器械制造商而言，可能是一个昂贵且耗时的障碍。",
      "IFUcare 深知您对<strong class='text-foreground'>更高效、更具成本效益且合规的解决方案</strong>的需求。正因如此，我们提供无缝的电子 IFU 服务，助力您告别纸质 IFU，迈向电子标签。",
      "IFUcare 帮助制造商通过数字平台分发其技术文档。我们提供<strong class='text-foreground'>单一供应商解决方案</strong>，由<strong class='text-foreground'>法规专家</strong>开发，专注于<strong class='text-foreground'>合规与风险管理</strong>。",
      "凭借用户友好的导航和强大的功能，IFUcare 可帮助您降低成本、确保合规并交付卓越的用户体验。我们可定制、多语言的界面确保全球用户均可访问，同时满足严格的法规标准。",
    ],
    whatImageAlt: "IFUcare 电子 IFU 平台界面预览",
    whyTitle: "为什么选择 IFUcare？",
    whyBenefits: [
      { title: "节省纸张与成本", text: "通过转向电子 IFU 服务，消除高昂的印刷、包装和物流成本。" },
      { title: "简化流程", text: "用精简的在线平台取代复杂的纸质流程，省时省力。" },
      { title: "提升客户体验", text: "让文档易于检索并实现版本控制，提升客户体验。" },
      { title: "降低风险", text: "减少责任和产品召回的可能性。我们的解决方案已获得公告机构认可，有助于您的电子 IFU（eIFU）顺利通过审核。" },
      { title: "合规无忧", text: "通过专为受监管行业打造的合规解决方案，满足欧盟、FDA 及其他法规要求。" },
    ],
    processTitle: "我们如何做到？",
    processIntro: "根据您的偏好，我们提供可配置的标准平台。流程的集成与验证是我们的核心能力。我们确保电子 IFU 解决方案的顺利实施与法规认可。",
    processImageAlt: "IFUcare 实施流程",
    extraSections: [
      {
        title: "客户评价",
        paragraphs: [
          "<blockquote class='border-l-4 border-primary/40 pl-4 italic text-muted-foreground'>\"Qarad 在我们近期从纸质使用说明转向电子使用说明的过程中给予了极大的帮助。他们提供非常专业的 eIFU 服务与平台。改用 eIFU 不仅为我们节省了成本，而且非常高效。当然，这对环境也有益。基本上每家公司都应该这样做。除了非常以客户为中心且灵活之外，Qarad 团队还拥有出色且最新的法规知识。\"<br/><br/><strong class='text-foreground not-italic'>Peter Kievit</strong><br/><span class='not-italic'>White Implants 总经理</span></blockquote>",
        ],
      },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "自 2008 年以来，我们一直是电子 IFU 服务的先行者，以单一供应商解决方案应对法规风险，该方案由专注于合规与风险管理的法规专家开发。我们的 eIFU 服务符合欧盟、FDA 及国家要求，确保您的法规保障。主要优势包括：",
    partnerBullets: [
      "法规专业能力 — 由专注于合规与风险管理的法规专家开发。",
      "欧盟与 FDA 合规 — 完全符合欧盟和 FDA 法规（IVD 的 MEDDEV 2.14/3 以及医疗器械的委员会条例 (EU) No. 207/2012）。",
      "全面可追溯性 — 记录所有用户和管理员操作（符合 21CFR11）。",
      "公告机构熟悉 — 通过 ISO13485 和 ISO27001 认证的供应商。软件开发依据 IEC80002。",
      "多语言支持 — 多语言界面及全球多语言支持。",
      "省时高效的实施 — 顺畅且省时高效的实施流程。",
      "可配置解决方案 — 为现成解决方案提供完全可配置的用户界面。",
    ],
    partnerImageAlt: "IFUcare — 为什么选择 QbD Group",
    externalCtaLabel: "访问 IFUcare.io",
  },

  /* ───── Scilife ───── */
  "scilife": {
    title: "Scilife™ — 质量管理软件",
    subtitle: "简化质量管理",
    description:
      "Scilife™ 是一款用户友好的一体化质量管理软件，旨在简化工作流程的方方面面——从文档管理、培训，到 CAPA 与 AI 驱动的 KPI。",
    whatTitle: "Scilife™ — 简化质量管理",
    whatParagraphs: [
      "管理质量流程可能令人不堪重负——保持合规、跨团队协作，以及跟踪 CAPA 和审核都绝非易事。但其实不必如此。<strong class='text-foreground'>Scilife™ 让质量管理变得简单</strong>，帮助您重获掌控、降低复杂度，并专注于最重要的事。",
      "Scilife™ 是一款用户友好的一体化质量管理软件，旨在简化工作流程的每个环节。从文档管理、培训，到 CAPA 与 AI 驱动的 KPI，Scilife 可确保<strong class='text-foreground'>全面的可追溯性、集成性与合规性</strong>。其直观的界面让您轻松上手，强大的功能让质量管理变得游刃有余。",
    ],
    whatImageAlt: "Scilife 质量管理软件平台",
    externalCtaLabel: "访问 Scilife.io",
    whyTitle: "让质量管理更轻松、更经济高效",
    whyBenefits: [
      { title: "易于部署，易于使用", text: "基于浏览器、以用户为中心的界面，可随时随地通过任何设备访问。用户可以轻松自如地操作。" },
      { title: "完整可追溯，全程掌控", text: "贯穿整个质量管理流程的完整可追溯性——每一步都被细致跟踪并完整记录。" },
      { title: "始终为审核做好准备", text: "完备的文档与实时报告功能，确保您随时处于可接受审核的状态。" },
      { title: "顺畅集成的 QA 流程", text: "在各部门之间无缝集成质量保证流程——文档控制、变更管理与风险评估。" },
      { title: "出色的用户体验", text: "引导式教程、功能说明与易用设计，让用户快速高效地上手。" },
    ],
    
    processTitle: "我们如何做到？",
    processIntro:
      "在 QbD Group，我们提供全面的工具以简化您的质量管理流程。我们的解决方案确保文档管理、培训、变更管理、质量事件、CAPA 和 KPI 的方方面面都得到无缝管理。",
    processSteps: [
      { title: "文档管理", text: "使用 MS Word 编辑文档，并通过可配置的审阅与批准流程推进，自动发送通知。" },
      { title: "培训", text: "随时掌握谁需要接受何种培训。定义需阅读的文档与需完成的任务，模块将全程跟踪。" },
      { title: "变更控制", text: "创建变更任务并分配至具体用户，由其逐一签署。所有任务完成后由 QA 审核并签字确认。" },
      { title: "质量事件", text: "配置多种事件类型，如偏差、不符合或投诉。执行根本原因分析，并决定是否需要 CAPA。" },
      { title: "CAPA", text: "制定 CAPA 计划、登记措施并检查有效性。自动通知确保及时跟进。" },
      { title: "KPI（AI）", text: "借助人工智能识别可改进领域。Scilife 实时分析数据，助力主动风险防控。" },
    ],
    processImageAlt: "Scilife — 全面的质量管理工具",
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro:
      "借助 Scilife™ 简化您的质量管理。无论您需要文档管理、培训管理、变更控制还是 CAPA 方面的支持，我们的团队都将随时为您提供帮助。",
    partnerBullets: [
      "一体化的质量管理软件，具备完整的可追溯性与合规性。",
      "AI 驱动的 KPI，助力主动的质量改进与风险防控。",
      "跨部门无缝集成——从文档管理到 CAPA。",
      "基于浏览器的直观界面，可随时随地通过任何设备访问。",
      "全面的审核就绪能力，配备实时报告功能。",
    ],
    partnerImageAlt: "Scilife — 为什么选择 QbD Group",
  },

  /* ───── 数据完整性 ───── */
  "data-integrity": {
    title: "数据完整性",
    subtitle: "确保数据全生命周期的准确性与一致性",
    description:
      "从设备与实验室系统到自动化系统与 IT 基础设施，数据完整性一如既往地至关重要。QbD Group 提供定制化专业能力，帮助贵公司符合最新法规要求。",
    whatTitle: "什么是数据完整性？",
    whatParagraphs: [
      "数据完整性涉及在数据整个生命周期中维护并确保<strong class='text-foreground'>数据的准确性与一致性</strong>。它是任何存储、处理或检索数据的 GxP 系统在设计、实施和使用过程中的基础要素。",
      "要维护数据完整性，遵守<strong class='text-foreground'>21 CFR Part 11 和 EU Annex 11</strong>等法规标准至关重要。这些法规支持 GxP 流程与设备的准确性和一致性，并与数据完整性的 ALCOA+ 原则保持一致：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>可归因 (Attributable)</strong>：数据可以追溯到生成它的人员或系统。</li><li><strong>清晰可读 (Legible)</strong>：数据清晰可读且持久保存。</li><li><strong>同步记录 (Contemporaneous)</strong>：数据在活动发生时即被记录。</li><li><strong>原始记录 (Original)</strong>：数据为首次记录版本或经认证的副本。</li><li><strong>准确无误 (Accurate)</strong>：数据正确、真实且无错误。</li><li><strong>可获取、持久、完整、一致 (Available, Enduring, Complete, Consistent)</strong>：扩展的 ALCOA+ 原则，确保数据在整个生命周期中保持可访问且可靠。</li></ul>",
    ],
    whatImageAlt: "数据完整性 — Software Solutions & Services — QbD Group",
    whyTitle: "确保数据准确性与一致性",
    whyBenefits: [
      { title: "法规合规", text: "遵守 21 CFR Part 11 和 EU Annex 11 可确保 GxP 流程中数据的准确性与一致性。" },
      { title: "数据安全", text: "在数据全生命周期中保护敏感数据免受未经授权的访问、篡改和丢失。" },
      { title: "风险缓解", text: "及早识别数据完整性问题，可避免代价高昂的法规发现和产品召回。" },
      { title: "审计就绪", text: "维护良好的数据完整性实践可确保您随时为法规检查做好准备。" },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "凭借多年在各类 GxP 环境中管理项目的经验，QbD Group 是您值得信赖的合作伙伴。我们帮助您：",
    partnerBullets: [
      "理解 GxP 流程中的数据流与数据使用。",
      "识别哪些数据对 GxP 合规至关重要。",
      "定义适当的数据完整性生命周期与方法。",
      "在确保遵守相关法规的同时，保障数据的完整性。",
    ],
    partnerImageAlt: "为什么选择 QbD Group — 数据完整性",
  },

  /* ───── 数字化 ───── */
  "digitalization": {
    title: "生命科学领域的数字化",
    subtitle: "利用合规的数字解决方案实现运营现代化与精简",
    description:
      "QbD Group 帮助 Life Sciences 企业实现运营现代化与精简。我们量身定制的数字化解决方案确保平稳过渡到更高效、更敏捷且更合规的流程，同时保障数据完整性与长期增长。",
    faqItems: [
      { q: "什么是生命科学领域的数字化？", a: "生命科学领域的数字化是将信息和流程从模拟格式转换为数字格式的过程。通过利用尖端技术，组织可以将手工和纸质工作流程转变为合规、自动化且数据驱动的系统。" },
      { q: "QbD Group 如何推进数字化项目？", a: "我们遵循结构化方法：需求评估、供应商与产品选型、方案设计、软件实施、验证与合规，以及变更管理 —— 确保每个阶段都能平稳过渡。" },
      { q: "QbD Group 在推荐数字解决方案时是否保持供应商中立？", a: "是的。我们采取技术中立的方法，比较广泛的解决方案 —— 从简单工具到复杂的企业平台 —— 根据您的具体需求、风险画像和预算推荐最合适的方案。" },
      { q: "您如何确保数字化转型期间的法规合规？", a: "我们依据 Annex 11、21 CFR Part 11 和 GAMP5 标准对每个系统进行验证，以在整个数字化转型过程中保障数据完整性和法规合规。" },
      { q: "QbD Group 与 delaware 的合作关系是什么？", a: "通过我们与 delaware 的战略合作伙伴关系，我们将深厚的 Life Sciences 专业知识与一流的数字技术相结合。共同推动组织实施 SAP 等强大系统，实现从第一天起就经过充分验证、合规且与您的流程高度一致。" },
    ],
    whatTitle: "生命科学领域的数字化是指什么？",
    whatParagraphs: [
      "数字化是将<strong class='text-foreground'>信息和流程从模拟格式转换为数字格式</strong>的过程。通过利用尖端技术，我们将手工和纸质工作流程转变为合规、自动化且数据驱动的系统。",
      "这一转变帮助组织<strong class='text-foreground'>提升效率、确保可追溯性并推动创新</strong>，同时加强法规合规和客户体验。我们在软件实施、验证和 IT 合作方面的专业知识，架起了法规要求、业务流程与技术之间的桥梁。",
    ],
    whatImageAlt: "生命科学领域的数字化 — Software Solutions & Services — QbD Group",
    whyTitle: "数字化为何对您重要",
    whyBenefits: [
      { title: "提升效率", text: "流程精简和减少人工工作可带来显著的成本节约和生产力提升。" },
      { title: "改善决策", text: "简化的数据管理和增强的可访问性支持基于数据的明智决策。" },
      { title: "增强敏捷性", text: "数字化系统使组织更容易适应市场变化和不断演变的客户需求。" },
    ],
    whyImageAlt: "数字化为何重要 — QbD Group",
    helpCards: {
      heading: "数字化的优势",
      intro: "数字化为您的运营带来可衡量的改进，从效率和创新到竞争优势。",
      cards: [
        { title: "精简运营", text: "减少人工流程、降低错误并提高效率。" },
        { title: "增强数据利用", text: "简化数据管理并提升可访问性，助力更智慧的决策。" },
        { title: "创新驱动", text: "通过先进的数字技术推动新解决方案和业务模式的发展。" },
        { title: "提升竞争力", text: "快速响应市场变化、满足客户期望并保持领先地位。" },
      ],
    },
    processTitle: "从评估到落地",
    processIntro: "在 QbD Group，我们引导您完成数字化转型的每个阶段，确保量身定制并无缝融入您的运营：",
    processSteps: [
      { title: "需求评估", text: "我们分析您当前的运营，识别数字化可以带来可衡量改进的领域。" },
      { title: "供应商与产品选型", text: "我们帮助您选择适合特定需求的数字解决方案。凭借技术中立的方法和深入的市场知识，我们比较广泛的解决方案 —— 从简单工具到复杂的企业平台。这一步往往至关重要且充满挑战。选错系统可能代价高昂；我们的专家确保您的投资在价值、功能和合规方面都能获得回报。" },
      { title: "方案设计", text: "我们根据您的需求和长期目标设计量身定制的数字化方案。" },
      { title: "软件实施", text: "我们实施一流的软件解决方案，实现任务自动化并优化工作流程。" },
      { title: "验证与合规", text: "我们依据 Annex 11、21 CFR Part 11 和 GAMP5 标准对每个系统进行验证，以保障数据完整性和法规合规。" },
      { title: "变更管理", text: "我们提供培训、文档和用户支持，确保团队能够顺利接纳新系统。" },
    ],
    processImageAlt: "数字化流程 — Software Solutions & Services — QbD Group",
    extraSections: [
      {
        title: "借助 QbD Group 与 delaware 加速您的数字化转型",
        paragraphs: [
          "通过我们与 delaware 的战略合作伙伴关系，我们将<strong class='text-foreground'>深厚的 Life Sciences 专业知识</strong>与<strong class='text-foreground'>一流的数字技术</strong>相结合。共同推动组织实施 SAP 等强大系统，实现从第一天起就经过充分验证、合规且与您的流程高度一致。",
        ],
        bullets: [
          { title: "更快实现价值", text: "加速数字化转型，无需因合规瓶颈而延误，同时确保全面的法规保障。" },
          { title: "随时可接受检查的系统", text: "每个解决方案从第一天起就经过验证并具备审计就绪能力。" },
          { title: "降低风险", text: "避免系统验证和法规错配中的常见陷阱。" },
          { title: "卓越运营", text: "通过集成、合规的数字系统推动效率提升。" },
        ],
        vimeoUrl: "https://player.vimeo.com/video/1169703841?h=ae35d4d0bb&badge=0&autopause=0&player_id=0&app_id=58479",
      },
      {
        title: "实践案例",
        paragraphs: [
          "我们帮助 <strong class='text-foreground'>Red Cross Flanders</strong> 选择并实施了一套符合其独特流程的全新质量管理系统 —— 从设计研究到完整验证和迁移计划。",
          "<a href='/en/case-studies/building-a-unified-qms-for-a-non-profit-lab-organization' class='text-primary hover:underline font-semibold'>阅读案例研究 →</a>",
        ],
      },
    ],
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "QbD Group 提供全面的数字化服务套件，旨在为您的组织创造最大价值：",
    partnerBullets: [
      "深厚的行业专业经验 — 在 pharma、biotech 和 medical devices 领域拥有可验证的业绩。",
      "供应商选型专业经验 — 我们的团队比较多种经过验证的系统，并就最适合您的流程、风险画像和预算的方案提供建议 —— 避免代价高昂的错配并确保长期成功。",
      "技术中立的方法 — 我们根据您的需求而非供应商关系推荐最合适的解决方案。",
      "全生命周期支持 — 从流程梳理和软件选型到验证、培训和持续合规。",
      "专注于长期成功 — 我们在每一步都与您合作，确保可持续的数字化转型。",
    ],
    partnerImageAlt: "为什么选择 QbD Group — 数字化",
  },

  /* ───── IT 基础设施确认 ───── */
  "it-infrastructure-qualification": {
    title: "IT 基础设施确认",
    subtitle: "证明 IT 组件符合 GxP 合规要求",
    description:
      "在当今高度受监管的环境中，确保 IT 基础设施的可靠性与合规性是一项日益严峻的挑战。QbD Group 提供 IT 基础设施确认专业能力，帮助贵公司符合最新法规要求。",
    whatTitle: "什么是 IT 基础设施确认？",
    whatParagraphs: [
      "IT 基础设施确认是一个<strong class='text-foreground'>证明为预期用途而开发的 IT 组件能够满足规定要求</strong>的过程，并确保系统的适用状态在系统生命周期的每个节点都得到保持。",
      "换言之，它（以高度确定性）证明<strong class='text-foreground'>IT 组件（如服务器、客户端和外围设备）能够满足各种平台的规定要求</strong>，无论这些平台是特定还是通用的。根据 21 CFR Part 11 和 Annex 11 等法规，IT 基础设施确认是一项法规要求。它适用于关键的 GxP 流程，并对 GxP 流程中使用的 IT 基础设施提供可靠性控制和洞察。",
    ],
    whatImageAlt: "IT 基础设施确认 — Software Solutions & Services — QbD Group",
    whyTitle: "量身定制的方法",
    whyBenefits: [
      { title: "灵活的服务", text: "用于确认 IT 基础设施的实际方法和生命周期由您决定，从而能够制定与您的特定业务需求相符的定制策略。最常用且公认的 CSV 方法是 GAMP5 方法论。" },
      { title: "法规合规", text: "根据 21 CFR Part 11 和 Annex 11 的要求，GxP 流程必须进行 IT 基础设施确认。" },
      { title: "运营可靠性", text: "确保您的 IT 基础设施在受监管环境中可靠运行且表现稳定。" },
      { title: "适应性", text: "这种灵活性确保您能够适应不断变化的技术格局和法规要求。" },
    ],
    whyImageAlt: "IT 基础设施确认 — QbD Group",
    partnerTitle: "为什么选择 QbD Group？",
    partnerIntro: "QbD Group 在各类 GxP 环境中拥有多年丰富的项目经验。我们帮助您：",
    partnerBullets: [
      "更好地了解哪些 IT 基础设施对 GxP 流程至关重要。",
      "更好地了解您的 IT 基础设施的运行方式（白盒方法）。",
      "确定生命周期和确认方法。",
      "确保您的 IT 基础设施可靠且符合所有相关法规 —— 这是迈向数据完整性的第一步。",
    ],
    partnerImageAlt: "为什么选择 QbD Group — IT 基础设施确认",
  },
};


export const getSwOfferingEs = (contentKey: string): SwOfferingI18n | undefined =>
  swOfferingsEs[contentKey];

export const getSwOfferingZh = (contentKey: string): SwOfferingI18n | undefined =>
  swOfferingsZh[contentKey];

export const getSwOfferingI18n = (contentKey: string, lang: SiteLang): SwOfferingI18n | undefined => {
  if (lang === "es") return swOfferingsEs[contentKey];
  if (lang === "zh-cn") return swOfferingsZh[contentKey];
  return undefined;
};
